import Vue from 'vue';
import axios from 'axios';
import API from '@/API';
import { getFolder } from '@/utils/general';

const state = {
  folders: [],
  teamFolders: [],
  mainFolder: {
    _id: 'my-stuff',
    name: `my-stuff`,
    total: 0
  },
  teamMainFolder: {
    _id: 'team',
    name: `team`,
    total: 0,
    isShared: true
  },
  totalDocuments: [],
  searchResults: null,
  fetchingContent: false,
  query: ''
};

const folderMap = {
  'my-stuff': state.mainFolder,
  team: state.teamMainFolder
};

const getters = {
  getChildFolders: state => parent => {
    switch (parent) {
      case 'my-stuff':
        return state.folders;
      case 'team':
        return state.teamFolders;
      default:
        return [];
    }
  }
};

const mutations = {
  // Search Mutations.
  SEARCH_FETCH_REQUEST(state) {
    state.fetchingContent = true;
  },
  SEARCH_FETCH_SUCCESS(state, { query, content }) {
    state.searchResults = content;
    state.query = query;
    state.fetchingContent = false;
  },
  RESET_SEARCH_RESULTS(state) {
    state.searchResults = null;
    state.query = '';
    state.fetchingContent = false;
  },
  SEARCH_FETCH_FAILURE(state) {
    state.fetchingContent = false;
  },
  // Documents Mutations.
  SET_DOCUMENTS(state, { folderId, docs, total = 0, isShared = false }) {
    switch (folderId) {
      // Both cases share the same logic but use a different identifier.
      case 'my-stuff':
      case 'team':
        folderMap[folderId].total = total;
        folderMap[folderId].documents = docs;
        return;
      default:
        break;
    }
    // If none on the cases matched then we have a real folder.
    const sourceFolders = isShared ? state.teamFolders : state.folders;
    let folder = getFolder(sourceFolders, folderId).folder;
    if (!folder) {
      folder = getFolder(state.teamFolders, folderId).folder;
    }
    folder.total = total;
    folder.documents = docs;
  },
  DELETE_DOCUMENT(state, { id, folderId }) {
    let idx = null;
    if (!folderId) {
      // Is root (my-stuff or team).
      let parentFolder = state.mainFolder;
      idx = parentFolder.documents?.findIndex(doc => doc._id === id);
      if (idx === -1 || idx === undefined) {
        parentFolder = state.teamMainFolder;
        // Check in the team folder in case it was not in the main one.
        idx = parentFolder.documents.findIndex(doc => doc._id === id);
      }
      if (idx >= 0) {
        parentFolder.documents.splice(idx, 1);
      }
      return;
    }
    let folder = getFolder(state.folders, folderId).folder;
    if (!folder) {
      folder = getFolder(state.teamFolders, folderId).folder;
    }
    idx = folder.documents.findIndex(doc => doc._id === id);
    if (idx >= 0) {
      folder.documents.splice(idx, 1);
    }
  },
  UPDATE_DOCUMENT_FOLDER(state, { update, oldFolder, newFolder }) {
    // Remnove old reference.
    if (oldFolder === `my-stuff`) {
      const idx = state.mainFolder?.documents?.findIndex(
        doc => doc._id === update._id
      );
      state.mainFolder?.documents?.splice(idx, 1);
    } else if (oldFolder === `team`) {
      const idx = state.teamMainFolder?.documents?.findIndex(
        doc => doc._id === update._id
      );
      state.teamMainFolder?.documents?.splice(idx, 1);
    } else {
      let oldFolderData = getFolder(state.folders, oldFolder);
      if (oldFolderData.folder === null) {
        oldFolderData = getFolder(state.teamFolders, oldFolder);
      }
      const index = oldFolderData.folder.documents?.findIndex(
        doc => doc._id === update._id
      );
      oldFolderData.folder.documents?.splice(index, 1);
    }
    // Add new reference.
    if (newFolder === `my-stuff`) {
      state.mainFolder.documents = state.mainFolder.documents || [];
      const exist = state.mainFolder.documents.find(
        doc => doc._id === update._id
      );
      if (!exist) {
        state.mainFolder.documents.push(update);
      }
    } else if (newFolder === `team`) {
      state.teamMainFolder.documents = state.teamMainFolder.documents || [];
      const exist = state.teamMainFolder.documents.find(
        doc => doc._id === update._id
      );
      if (!exist) {
        state.teamMainFolder.documents.push(update);
      }
    } else {
      let newFolderData = getFolder(state.folders, newFolder);
      if (newFolderData.folder === null) {
        newFolderData = getFolder(state.teamFolders, newFolder);
      }
      newFolderData.folder.documents = newFolderData.folder.documents || [];
      const exist = newFolderData.folder.documents.find(
        doc => doc._id === update._id
      );
      if (!exist) {
        newFolderData.folder.documents.push(update);
      }
    }
  },
  ADD_DOCUMENT(state, { folderId, newDoc }) {
    if (folderId === `my-stuff`) {
      state.mainFolder.documents.push(newDoc);
      return;
    }
    if (folderId === `team`) {
      state.teamMainFolder.documents.push(newDoc);
      return;
    }
    let parentFolder = getFolder(state.folders, folderId).folder;
    if (!parentFolder) {
      parentFolder = getFolder(state.teamFolders, folderId).folder;
    }
    parentFolder.documents.push(newDoc);
  },
  UPDATE_DOCUMENT(state, { folderId, update }) {
    let documents;
    if (folderId === `my-stuff` || folderId === 'team') {
      try {
        let mainFolder, mainFolderProp;
        switch (folderId) {
          case 'my-stuff':
            mainFolder = state.mainFolder;
            mainFolderProp = 'mainFolder';
            break;
          case 'team':
            mainFolder = state.teamMainFolder;
            mainFolderProp = 'teamMainFolder';
            break;
        }
        documents = mainFolder.documents;
        const idx = documents.findIndex(doc => doc._id === update._id);
        documents[idx] = update;
        mainFolder.documents = documents;
        Vue.set(state, mainFolderProp, mainFolder);
      } catch (error) {
        console.error(error);
      }
      return;
    }
    const folders = state.folders;
    const parentFolder = getFolder(folders, folderId).folder;
    documents = parentFolder?.documents;
    if (!documents) {
      return;
    }
    const idx = documents.findIndex(doc => doc._id === update._id);
    documents[idx] = update;
    parentFolder.documents = documents;
    Vue.set(state, 'folders', folders);
    return;
  },
  // Folders Mutations.
  SET_FOLDERS(state, { folders }) {
    state.folders = folders;
  },
  SET_TEAM_FOLDERS(state, { folders }) {
    state.teamFolders = folders;
  },
  ADD_FOLDER(state, { newFolder }) {
    if (!newFolder.parent) {
      // Is root (my-stuff or teams).
      if (newFolder.isShared) {
        state.teamFolders.push(newFolder);
      } else {
        state.folders.push(newFolder);
      }
      return;
    }
    let parentFolder = getFolder(state.folders, newFolder.parent).folder;
    if (!parentFolder) {
      parentFolder = getFolder(state.teamFolders, newFolder.parent).folder;
    }
    parentFolder.folders.push(newFolder);
  },
  REMOVE_FOLDER(state, { targetFolder }) {
    const targetFolderKey = targetFolder.isShared ? 'teamFolders' : 'folders';
    const folders = state[targetFolderKey];
    for (let i = 0; i < folders.length; i++) {
      const folder = folders[i];
      if (folder._id === targetFolder._id) {
        folders.splice(i, 1);
        break;
      } else {
        for (let ii = 0; ii < folder.folders.length; ii++) {
          const subfolder = folder.folders[ii];
          if (subfolder._id === targetFolder._id) {
            folder.folders.splice(ii, 1);
            break;
          }
        }
      }
    }
    state = { ...state };
    state[targetFolderKey] = folders;
  }
};

const actions = {
  // Search Actions.
  async searchContent({ commit }, { query, includeShared = false }) {
    if (!query) {
      // If empty query, clear searchResults from state.
      commit(`SEARCH_FETCH_SUCCESS`, { query, content: null });
      return;
    }
    try {
      commit(`SEARCH_FETCH_REQUEST`);
      const response = await axios.get(API.searchContent(), {
        params: {
          query,
          includeShared
        }
      });
      if (response?.status === 200) {
        commit(`SEARCH_FETCH_SUCCESS`, {
          query,
          content: response.data
        });
        return response.data;
      }
    } catch (error) {
      commit(`SEARCH_FETCH_FAILURE`);
      console.error('Error while searching for content: ', error);
    }
  },
  // Folders Actions.
  async requestFolders({ commit }, params = {}) {
    try {
      const response = await axios.get(API.folders(), { params });
      if (response?.status === 200) {
        const folders = response.data;
        // Add the collapsed property to each folder
        folders.forEach((folder, index) => {
          folders[index].isCollapsed = true;
        });
        if (params.hasOwnProperty('isShared')) {
          const commitMethod = params.isShared
            ? 'SET_TEAM_FOLDERS'
            : 'SET_FOLDERS';
          commit(commitMethod, { folders });
        } else {
          const teamFolders = folders.filter(f => f.isShared);
          const privateFolders = folders.filter(f => !f.isShared);
          commit('SET_TEAM_FOLDERS', { folders: teamFolders });
          commit('SET_FOLDERS', { folders: privateFolders });
        }
      }
    } catch (error) {
      console.error('Error while getting folders: ', error);
    }
  },
  async createFolder({ commit }, params) {
    try {
      const response = await axios.post(API.folders(), params);
      if (response?.status === 200) {
        commit(`ADD_FOLDER`, { newFolder: response.data.newFolder });
        return response;
      }
    } catch (error) {
      console.error('Error while creating folder: ', error);
    }
  },
  async updateFolder({ commit }, params) {
    try {
      const response = await axios.put(API.folders(), params);
      if (response?.status === 200) {
        const allFolders = response.data;
        // Add the collapsed property to each folder
        allFolders.forEach((folder, index) => {
          allFolders[index].isCollapsed = true;
        });
        const personalFolders = allFolders.filter(folder => !folder.isShared);
        const teamFolders = allFolders.filter(folder => folder.isShared);
        commit(`SET_FOLDERS`, { folders: personalFolders });
        commit(`SET_TEAM_FOLDERS`, { folders: teamFolders });
        return response;
      }
    } catch (error) {
      console.error('Error while updating folder: ', error);
    }
  },
  async deleteFolder({ commit }, { id }) {
    try {
      const response = await axios.delete(API.folders(id));
      if (response?.status === 200) {
        commit(`REMOVE_FOLDER`, { targetFolder: response.data });
        return response;
      }
    } catch (error) {
      console.error('Error while deleting folder: ', error);
    }
  },
  // Documents Actions.
  async requestDocuments({ commit }, { folderId, isShared } = {}) {
    try {
      const url = folderId
        ? `${API.documents}?folder=${folderId}&all=true&isShared=${isShared}`
        : `${API.documents}?all=true&isShared=${isShared}`;
      const response = await axios.get(url);
      if (response?.status === 200 && folderId) {
        const docs = response.data.documents;
        const total = response.data?.meta?.total;
        commit(`SET_DOCUMENTS`, { folderId, docs, total, isShared });
      }
      return response;
    } catch (error) {
      console.error('Error while getting documents: ', error);
    }
  },
  /**
   * Deletes a document from personal and team folders.
   * @param { Object } commit Receives the commit object for the store.
   * @param { Object } id An object with the ID of the document to delete.
   * @returns { Boolean } To identify if the execution was successful.
   */
  async deleteDocument({ commit }, { id }) {
    try {
      const response = await axios.delete(`${API.documents}${id}`);
      if (response?.status === 200) {
        commit(`DELETE_DOCUMENT`, {
          id: response.data.id,
          folderId: response.data.folder
        });
      }
      return true;
    } catch (error) {
      console.error('Error while deleting document: ', error);
      return false;
    }
  },
  async updateDocument({ commit, state, dispatch }, { id, params }) {
    try {
      const endpoint = API.getDocumentsEndpoint(id);
      const response = await axios.patch(endpoint, params);
      if (response?.status === 200) {
        if (params.newFolder) {
          // If newFolder is present, means it was moved from folder,
          // let's update that change in our sore.
          let folderData;
          switch (params.newFolder) {
            case 'my-stuff':
              folderData = { folder: { ...state.mainFolder, _id: 'my-stuff' } };
              break;
            case 'team':
              folderData = { folder: { ...state.teamMainFolder, _id: 'team' } };
              break;
            default:
              folderData = getFolder(state.folders, params.newFolder);
              if (folderData.folder === null) {
                folderData = getFolder(state.teamFolders, params.newFolder);
              }
              break;
          }
          if (!folderData.folder.documents) {
            // If no documents are present in folder, lets request them.
            await dispatch('requestDocuments', {
              folderId: folderData.folder._id
            });
          }
          commit(`UPDATE_DOCUMENT_FOLDER`, {
            update: response.data,
            oldFolder: params.oldFolder,
            newFolder: params.newFolder,
            isShared: params.isShared
          });
        }
      }
    } catch (error) {
      console.error('Error while updating document: ', error);
    }
  },
  async uploadDocument({ commit, dispatch, state }, { id, folder, data, cb }) {
    const endpoint = API.getDocumentsEndpoint(id || '');
    const method = id ? 'put' : 'post';
    // Populate store before upload / update.
    if (!state.folders.length) {
      await dispatch('requestFolders');
      await dispatch('requestDocuments', { folderId: folder });
    }
    const { data: document } = await axios[method](endpoint, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: cb
    });
    if (id) {
      commit(`UPDATE_DOCUMENT`, { folderId: folder, update: document });
    } else {
      commit(`ADD_DOCUMENT`, { folderId: folder, newDoc: document });
    }
    return document;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
